import { authFetch } from '../../api/csrf';
import React, { useState, useEffect } from 'react';
import {
    Calendar,
    FileText,
    AlertCircle,
    CheckCircle,
    Upload,
    Heart,
    Briefcase,
    Loader2,
    Eye,
    Download,
    Trash2,
} from 'lucide-react';
import { toast } from 'react-hot-toast';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import essService from '../../api/services/essService';
import { post } from '../../api/apiClient';

// ─── Shared micro-components ─────────────────────────────────────────────────

const CHAR_LIMITS = {
    reason: 100,
    detailedReason: 500,
    handoverNotes: 500,
    buyoutReason: 200,
    emergencyContactName: 100,
    emergencyContactPhone: 15,
    emergencyContactRelationship: 100,
    emergencyContactEmail: 100,
};

const CharCounter = ({ current, limit }) => (
    <div className="text-[10px] text-gray-400 mt-1 text-right">
        {current}/{limit} characters
    </div>
);

const ErrorMessage = ({ message, touched }) =>
    message && touched ? (
        <p className="text-[10px] text-red-500 mt-1 font-medium select-none animate-in fade-in slide-in-from-top-1">
            {message}
        </p>
    ) : null;

const Label = ({ children, required, error }) => (
    <label
        className={`block text-sm font-medium mb-1.5 transition-colors ${error ? 'text-red-600' : 'text-gray-700'
            }`}
    >
        {children}{' '}
        {required && <span className="text-red-500 ml-0.5">*</span>}
    </label>
);

const SectionTitle = ({ icon: Icon, title, description }) => (
    <div className="flex items-center space-x-3 mb-6 pb-4 border-b border-gray-100">
        <div className="p-2 bg-blue-50 rounded-lg text-blue-600">
            <Icon className="w-5 h-5" />
        </div>
        <div>
            <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
            {description && <p className="text-sm text-gray-500">{description}</p>}
        </div>
    </div>
);

// ─── Initial form state ───────────────────────────────────────────────────────

const INITIAL_FORM = {
    reason: '',
    detailedReason: '',
    handoverNotes: '',
    proposedLastWorkingDate: '',
    noticePeriod: '30',
    noticeStartDate: new Date().toISOString().split('T')[0],
    noticeEndDate: '',
    buyoutRequest: 'No',
    buyoutReason: '',
    resignationCategory: 'Better Opportunity',
    attachment: null,
    confirmVoluntary: false,
    confirmPolicy: false,
    confirmAccurate: false,
    exitInterview: false,
    emergencyContactName: '',
    emergencyContactPhone: '',
    emergencyContactRelationship: '',
    emergencyContactEmail: '',
    submissionDate: new Date().toISOString().split('T')[0],
    currentStatus: 'Draft',
    managerApprovalStatus: 'Pending',
    hrApprovalStatus: 'Pending',
};

// ─── Field validation ─────────────────────────────────────────────────────────

const validateField = (name, value) => {
    switch (name) {
        case 'reason':
            if (!value?.trim()) return 'Reason for resignation is required';
            break;
        case 'detailedReason':
            if (!value?.trim()) return 'Please provide brief details';
            break;
        case 'proposedLastWorkingDate':
            if (!value) return 'Last working date is required';
            break;
        case 'emergencyContactName':
            if (!value?.trim()) return 'Name is required';
            break;
        case 'emergencyContactPhone':
            if (!value?.trim()) return 'Phone is required';
            if (value.replace(/[^0-9]/g, '').length > 15) return 'Phone must be at most 15 digits';
            break;
        case 'emergencyContactRelationship':
            if (!value?.trim()) return 'Relationship is required';
            break;
        case 'emergencyContactEmail':
            if (value?.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value))
                return 'Invalid email address';
            break;
        // ── Declaration checkboxes (mandatory) ──────────────────────────────
        case 'confirmVoluntary':
            if (!value) return 'You must confirm this to proceed';
            break;
        case 'confirmPolicy':
            if (!value) return 'You must confirm this to proceed';
            break;
        case 'confirmAccurate':
            if (!value) return 'You must confirm this to proceed';
            break;
        default:
            break;
    }
    return '';
};

// ─── Component ────────────────────────────────────────────────────────────────

/**
 * ResignationForm
 *
 * Props:
 *  - requests   {Array}    Existing resignation requests (used to guard the
 *                          submit button when a pending/approved one exists).
 *  - onSuccess  {Function} Called after the resignation is submitted successfully.
 */
const ResignationForm = ({ requests = [], onSuccess, onBehalfOf }) => {
    const queryClient = useQueryClient();

    const [form, setForm] = useState(INITIAL_FORM);
    const [errors, setErrors] = useState({});
    const [touched, setTouched] = useState({});
    const [isDragging, setIsDragging] = useState(false);
    const [isUploading, setIsUploading] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);

    const ensureFilenameHasExtension = (filename, mimeType = '') => {
        if (!filename) return 'attachment';

        const mimeExtensionMap = {
            'application/pdf': '.pdf',
            'image/jpeg': '.jpg',
            'image/png': '.png',
            'image/webp': '.webp',
        };

        if (/\.[a-z0-9]+$/i.test(filename)) {
            return filename;
        }

        return `${filename}${mimeExtensionMap[mimeType] || ''}`;
    };

    const triggerBrowserDownload = (url, filename) => {
        const link = document.createElement('a');
        link.href = url;
        link.download = filename;
        link.target = '_blank';
        link.rel = 'noopener noreferrer';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    const getAttachmentFilename = (attachment, mimeType = '') =>
        ensureFilenameHasExtension(
            attachment?.original_filename || attachment?.originalName || attachment?.name || 'attachment',
            mimeType
        );

    const handlePreviewAttachment = async attachment => {
        if (!attachment?.url) {
            toast.error('No file available');
            return;
        }

        try {
            const response = await authFetch(attachment.url);
            if (!response.ok) {
                throw new Error('Failed to fetch attachment');
            }

            const blob = await response.blob();
            const blobUrl = URL.createObjectURL(blob);
            const isPreviewable = blob.type === 'application/pdf' || blob.type.startsWith('image/');

            if (isPreviewable) {
                const previewWindow = window.open(blobUrl, '_blank', 'noopener,noreferrer');
                if (!previewWindow) {
                    triggerBrowserDownload(blobUrl, getAttachmentFilename(attachment, blob.type));
                }
            } else {
                triggerBrowserDownload(blobUrl, getAttachmentFilename(attachment, blob.type));
            }

            setTimeout(() => URL.revokeObjectURL(blobUrl), 1000);
        } catch {
            triggerBrowserDownload(attachment.url, getAttachmentFilename(attachment));
        }
    };

    const handleDownloadAttachment = async attachment => {
        if (!attachment?.url) {
            toast.error('No file available');
            return;
        }

        try {
            const response = await authFetch(attachment.url);
            if (!response.ok) {
                throw new Error('Failed to fetch attachment');
            }

            const blob = await response.blob();
            const blobUrl = URL.createObjectURL(blob);
            triggerBrowserDownload(blobUrl, getAttachmentFilename(attachment, blob.type));
            setTimeout(() => URL.revokeObjectURL(blobUrl), 1000);
        } catch {
            triggerBrowserDownload(attachment.url, getAttachmentFilename(attachment));
        }
    };

    // ── Auto-calculate notice end date ─────────────────────────────────────────
    useEffect(() => {
        const date = new Date(form.noticeStartDate);
        date.setDate(date.getDate() + parseInt(form.noticePeriod || 30));
        const endDate = date.toISOString().split('T')[0];
        setForm(prev => ({
            ...prev,
            noticeEndDate: endDate,
            proposedLastWorkingDate: prev.proposedLastWorkingDate || endDate,
        }));
    }, [form.noticeStartDate, form.noticePeriod]);

    // ── Field change handler ───────────────────────────────────────────────────
    const handleChange = async e => {
        const { name, value, type, checked, files } = e.target;

        if (type === 'file' && files?.[0]) {
            const file = files[0];
            const validTypes = ['application/pdf', 'image/jpeg', 'image/png', 'image/webp'];
            if (!validTypes.includes(file.type)) {
                toast.error('Only PDF and Image files are allowed');
                return;
            }
            if (file.size > 25 * 1024 * 1024) {
                toast.error('File size must be less than 25MB');
                return;
            }
            setIsUploading(true);
            try {
                const res = await essService.uploadDocument(file);
                if (res.success) {
                    setForm(prev => ({ ...prev, [name]: res.data }));
                    toast.success('File uploaded successfully');
                } else {
                    toast.error(res.error || 'Upload failed');
                }
            } catch {
                toast.error('Error uploading file');
            } finally {
                setIsUploading(false);
            }
            return;
        }

        let finalValue = value;
        if (
            type !== 'checkbox' &&
            type !== 'file' &&
            name !== 'resignationCategory' &&
            name !== 'buyoutRequest' &&
            type !== 'date'
        ) {
            if (name === 'emergencyContactPhone') {
                // Allow digits and leading + only; strip everything else
                finalValue = value.replace(/[^0-9+]/g, '').replace(/(?!^)\+/g, '');
            } else if (name === 'emergencyContactEmail') {
                finalValue = value.replace(/[^a-zA-Z0-9@._\-+]/g, '');
            } else {
                finalValue = value.replace(/[^a-zA-Z0-9\s.,?!'()\-#$@:;]/g, '');
            }
            const limit = CHAR_LIMITS[name];
            if (limit && finalValue.length > limit) return;
        }

        setForm(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : finalValue,
        }));

        // Clear the field's error as soon as the user fixes it
        if (errors[name]) {
            setErrors(prev => {
                const next = { ...prev };
                delete next[name];
                return next;
            });
        }
        // For checkboxes: re-validate immediately so error clears on check
        if (type === 'checkbox') {
            const err = validateField(name, checked);
            setErrors(prev => ({ ...prev, [name]: err }));
            setTouched(prev => ({ ...prev, [name]: true }));
        }
    };

    const handleBlur = e => {
        const { name, value } = e.target;
        setTouched(prev => ({ ...prev, [name]: true }));
        const err = validateField(name, value);
        setErrors(prev => ({ ...prev, [name]: err }));
    };

    // ── Drag & drop ───────────────────────────────────────────────────────────
    const handleDragOver = e => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(true);
    };
    const handleDragLeave = e => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(false);
    };
    const handleDrop = async e => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(false);
        const file = e.dataTransfer.files?.[0];
        if (!file) return;
        const validTypes = ['application/pdf', 'image/jpeg', 'image/png', 'image/webp'];
        if (!validTypes.includes(file.type)) {
            toast.error('Only PDF and Image files are allowed');
            return;
        }
        if (file.size > 25 * 1024 * 1024) {
            toast.error('File size must be less than 25MB');
            return;
        }
        setIsUploading(true);
        try {
            const res = await essService.uploadDocument(file);
            if (res.success) {
                setForm(prev => ({ ...prev, attachment: res.data }));
                toast.success('File uploaded successfully');
            } else {
                toast.error(res.error || 'Upload failed');
            }
        } catch {
            toast.error('Error uploading file');
        } finally {
            setIsUploading(false);
        }
    };

    const handleDeleteAttachment = async e => {
        e.stopPropagation();
        if (!form.attachment) return;
        setIsDeleting(true);
        try {
            const { public_id, resource_type } = form.attachment;
            const res = await essService.deleteTempDocument(public_id, resource_type);
            if (res.success) {
                setForm(prev => ({ ...prev, attachment: null }));
                toast.success('File removed');
            } else {
                toast.error(res.error || 'Failed to remove file');
            }
        } catch {
            toast.error('Error removing file');
        } finally {
            setIsDeleting(false);
        }
    };

    // ── Full-form validation ───────────────────────────────────────────────────
    const validateForm = () => {
        const newErrors = {};
        const newTouched = {};
        Object.keys(form).forEach(key => {
            const err = validateField(key, form[key]);
            if (err) newErrors[key] = err;
            newTouched[key] = true;
        });
        setErrors(newErrors);
        setTouched(newTouched);
        if (Object.keys(newErrors).length > 0) {
            toast.error('Please fix the errors in the form');
            return false;
        }
        return true;
    };

    // ── Submit mutation ────────────────────────────────────────────────────────
    const submitMutation = useMutation({
        mutationFn: async (formData) => {
            // ── HR mode: submit on behalf of a selected employee ──────────────
            if (onBehalfOf) {
                const payload = {
                    employeeId: onBehalfOf.employeeId,
                    resignationType: 'voluntary',
                    // Date fields
                    lastWorkingDate: formData.proposedLastWorkingDate,
                    proposedLastWorkingDate: formData.proposedLastWorkingDate,
                    noticeStartDate: formData.noticeStartDate || new Date().toISOString().split('T')[0],
                    noticeEndDate: formData.noticeEndDate || '',
                    // Notice & buyout
                    noticePeriod: parseInt(formData.noticePeriod || 30),
                    buyoutRequest: formData.buyoutRequest || 'No',
                    buyoutReason: formData.buyoutReason || '',
                    // Reason & category
                    reason: formData.reason,
                    detailedReason: formData.detailedReason || '',
                    resignationCategory: formData.resignationCategory || 'Other',
                    // Other fields
                    handoverNotes: formData.handoverNotes || '',
                    personalEmail: onBehalfOf.user?.email || onBehalfOf.contactInfo?.personalEmail || '',
                    willingToParticipateInExitInterview: !!formData.exitInterview,
                    declarations: {
                        confirmVoluntary: !!formData.confirmVoluntary,
                        confirmPolicy: !!formData.confirmPolicy,
                        confirmAccurate: !!formData.confirmAccurate,
                    },
                    emergencyContact: {
                        name: formData.emergencyContactName || '',
                        phone: formData.emergencyContactPhone || '',
                        relationship: formData.emergencyContactRelationship || '',
                        email: formData.emergencyContactEmail || '',
                    },
                    attachments: formData.attachment
                        ? [{
                            fileName: formData.attachment.public_id,
                            originalName: formData.attachment.original_filename,
                            filePath: formData.attachment.url,
                            fileSize: formData.attachment.bytes,
                        }]
                        : [],
                };
                const json = await post('/exit', payload);
                return { success: true, data: json.data };
            }

            // ── Self-service mode (original behaviour, unchanged) ─────────────
            const { attachment, ...rest } = formData;
            const payload = {
                ...rest,
                attachments: attachment
                    ? [
                        {
                            fileName: attachment.public_id,
                            originalName: attachment.original_filename,
                            filePath: attachment.url,
                            fileSize: attachment.bytes,
                        },
                    ]
                    : [],
            };
            return essService.submitResignation(payload);
        },
        onSuccess: res => {
            if (res.success) {
                toast.success(
                    onBehalfOf
                        ? `Exit request submitted for ${onBehalfOf.personalInfo?.firstName} ${onBehalfOf.personalInfo?.lastName}`
                        : 'Resignation submitted successfully'
                );
                queryClient.invalidateQueries({ queryKey: ['resignationRequests'] });
                onSuccess?.();
            } else {
                toast.error(res.error || 'Failed to submit resignation');
            }
        },
        onError: error => {
            toast.error(error.message || 'An unexpected error occurred');
        },
    });

    const handleSubmit = e => {
        e.preventDefault();
        // Run full validation — marks every field as touched so errors render
        if (!validateForm()) {
            // Scroll the first error into view
            const firstErr = document.querySelector('[data-field-error]');
            firstErr?.scrollIntoView({ behavior: 'smooth', block: 'center' });
            return;
        }
        submitMutation.mutate(form);
    };

    const hasActiveRequest = requests.some(r =>
        ['pending', 'approved'].includes(r.status)
    );

    // Only block submission while a request is in-flight, an active request
    // already exists, or mandatory declarations are not confirmed.
    const isSubmitDisabled =
        submitMutation.isPending ||
        hasActiveRequest ||
        !form.confirmVoluntary ||
        !form.confirmPolicy ||
        !form.confirmAccurate;

    // ─────────────────────────────────────────────────────────────────────────
    return (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* ── Left: Main form sections ────────────────────────────────────── */}
            <div className="lg:col-span-2 space-y-6">

                {/* 1. Resignation Details */}
                <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
                    <SectionTitle
                        icon={FileText}
                        title="Resignation Details"
                        description="Please provide the primary reason for your decision."
                    />
                    <div className="space-y-5">
                        {/* Reason */}
                        <div>
                            <Label required error={errors.reason && touched.reason}>Reason</Label>
                            <input
                                type="text"
                                name="reason"
                                className={`w-full px-4 py-2.5 bg-white border rounded-lg focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all placeholder:text-gray-400 ${errors.reason && touched.reason
                                    ? 'border-red-300 bg-red-50/10'
                                    : 'border-gray-200'
                                    }`}
                                placeholder="e.g. Exploring better career opportunities"
                                value={form.reason}
                                onChange={handleChange}
                                onBlur={handleBlur}
                            />
                            <div className="flex justify-between items-start">
                                <ErrorMessage message={errors.reason} touched={touched.reason} />
                                <CharCounter current={form.reason.length} limit={CHAR_LIMITS.reason} />
                            </div>
                        </div>

                        {/* Details */}
                        <div>
                            <Label required error={errors.detailedReason && touched.detailedReason}>Details</Label>
                            <textarea
                                name="detailedReason"
                                rows={4}
                                className={`w-full px-4 py-2.5 bg-white border rounded-lg focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all placeholder:text-gray-400 ${errors.detailedReason && touched.detailedReason
                                    ? 'border-red-300 bg-red-50/10'
                                    : 'border-gray-200'
                                    }`}
                                placeholder="Please provide specific details about your decision to resign..."
                                value={form.detailedReason}
                                onChange={handleChange}
                                onBlur={handleBlur}
                            />
                            <div className="flex justify-between items-start">
                                <ErrorMessage message={errors.detailedReason} touched={touched.detailedReason} />
                                <CharCounter current={form.detailedReason.length} limit={CHAR_LIMITS.detailedReason} />
                            </div>
                        </div>

                        {/* Handover Notes */}
                        <div>
                            <Label>Handover Notes</Label>
                            <textarea
                                name="handoverNotes"
                                rows={3}
                                className="w-full px-4 py-2.5 bg-white border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all placeholder:text-gray-400"
                                placeholder="List key pending tasks, project statuses, or critical documentation to be handed over..."
                                value={form.handoverNotes}
                                onChange={handleChange}
                                onBlur={handleBlur}
                            />
                            <CharCounter current={form.handoverNotes.length} limit={CHAR_LIMITS.handoverNotes} />
                        </div>
                    </div>
                </div>

                {/* 2. Notice Period & Buyout */}
                <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
                    <SectionTitle
                        icon={Calendar}
                        title="Notice Period & Buyout"
                        description="Timeline and shortfall requests."
                    />
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div>
                            <Label required error={errors.proposedLastWorkingDate && touched.proposedLastWorkingDate}>
                                Proposed Last Working Date
                            </Label>
                            <input
                                type="date"
                                name="proposedLastWorkingDate"
                                className={`w-full px-4 py-2.5 bg-white border rounded-lg focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all ${errors.proposedLastWorkingDate && touched.proposedLastWorkingDate
                                    ? 'border-red-300 bg-red-50/10'
                                    : 'border-gray-200'
                                    }`}
                                value={form.proposedLastWorkingDate}
                                min={new Date().toISOString().split('T')[0]}
                                onChange={handleChange}
                                onBlur={handleBlur}
                            />
                            <ErrorMessage
                                message={errors.proposedLastWorkingDate}
                                touched={touched.proposedLastWorkingDate}
                            />
                        </div>
                        <div>
                            <Label>Notice Start Date</Label>
                            <input
                                type="date"
                                readOnly
                                className="w-full px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-lg text-gray-500 cursor-not-allowed"
                                value={form.noticeStartDate}
                            />
                        </div>
                        <div>
                            <Label>Notice Period (Days)</Label>
                            <input
                                type="number"
                                readOnly
                                className="w-full px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-lg text-gray-500 cursor-not-allowed"
                                value={form.noticePeriod}
                            />
                        </div>
                        <div>
                            <Label>Notice End Date</Label>
                            <input
                                type="date"
                                readOnly
                                className="w-full px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-lg text-gray-500 cursor-not-allowed"
                                value={form.noticeEndDate}
                            />
                        </div>
                        <div>
                            <Label>Buyout Request</Label>
                            <select
                                name="buyoutRequest"
                                className="w-full px-4 py-2.5 bg-white border border-gray-200 rounded-lg"
                                value={form.buyoutRequest}
                                onChange={handleChange}
                            >
                                <option>No</option>
                                <option>Yes</option>
                            </select>
                        </div>
                        {form.buyoutRequest === 'Yes' && (
                            <div className="animate-in fade-in slide-in-from-top-2 duration-300">
                                <Label>Buyout Reason</Label>
                                <input
                                    type="text"
                                    name="buyoutReason"
                                    className="w-full px-4 py-2.5 border border-gray-200 rounded-lg placeholder:text-gray-400 focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all"
                                    placeholder="Explain why you are requesting to waive the notice period..."
                                    value={form.buyoutReason}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                />
                                <CharCounter current={form.buyoutReason.length} limit={CHAR_LIMITS.buyoutReason} />
                            </div>
                        )}
                    </div>
                </div>

                {/* 3. Classification */}
                <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
                    <SectionTitle icon={Briefcase} title="Classification" />
                    <div className="grid grid-cols-1 gap-6">
                        <div>
                            <Label>Resignation Category</Label>
                            <select
                                name="resignationCategory"
                                className="w-full px-4 py-2.5 bg-white border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all"
                                value={form.resignationCategory}
                                onChange={handleChange}
                            >
                                {[
                                    'Better Opportunity',
                                    'Personal',
                                    'Health',
                                    'Higher Studies',
                                    'Relocation',
                                    'Retirement',
                                    'Other',
                                ].map(cat => (
                                    <option key={cat}>{cat}</option>
                                ))}
                            </select>
                        </div>

                        {/* Attachment upload */}
                        <div>
                            <Label>Attachment (PDF or Image, max 25MB)</Label>
                            <div
                                className={`relative px-4 py-8 border-2 border-dashed rounded-xl text-center transition-all ${isDragging ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                                    } ${isUploading || isDeleting ? 'cursor-wait opacity-70' : 'cursor-pointer'}`}
                                onDragOver={!isUploading && !isDeleting ? handleDragOver : undefined}
                                onDragLeave={!isUploading && !isDeleting ? handleDragLeave : undefined}
                                onDrop={!isUploading && !isDeleting ? handleDrop : undefined}
                                onClick={() =>
                                    !isUploading && !isDeleting && !form.attachment &&
                                    document.getElementById('resignation-file-upload').click()
                                }
                            >
                                <input
                                    id="resignation-file-upload"
                                    type="file"
                                    name="attachment"
                                    className="sr-only"
                                    accept=".pdf,image/*"
                                    onChange={handleChange}
                                    disabled={isUploading || isDeleting}
                                />
                                <div className="flex flex-col items-center">
                                    {isUploading || isDeleting ? (
                                        <div className="flex flex-col items-center">
                                            <Loader2 className="w-8 h-8 text-blue-500 animate-spin mb-3" />
                                            <span className="text-sm font-medium text-gray-600">
                                                {isUploading ? 'Uploading...' : 'Removing...'}
                                            </span>
                                        </div>
                                    ) : (
                                        <>
                                            <div className="p-3 bg-gray-100 rounded-full mb-3">
                                                <Upload className={`w-6 h-6 ${isDragging ? 'text-blue-500' : 'text-gray-500'}`} />
                                            </div>
                                            {form.attachment ? (
                                                <div className="space-y-3 w-full">
                                                    <div className="flex items-center justify-center space-x-2 text-sm text-green-600 font-medium">
                                                        <CheckCircle className="w-4 h-4" />
                                                        <span className="truncate max-w-[200px]">
                                                            {form.attachment.original_filename}
                                                        </span>
                                                    </div>
                                                    <div className="flex items-center justify-center space-x-3">
                                                        <button
                                                            type="button"
                                                            onClick={e => {
                                                                e.stopPropagation();
                                                                handleDownloadAttachment(form.attachment);
                                                            }}
                                                            className="flex items-center px-3 py-1.5 bg-emerald-50 text-emerald-600 text-xs font-bold rounded-lg hover:bg-emerald-100 transition-colors"
                                                        >
                                                            <Download className="w-3.5 h-3.5 mr-1.5" />
                                                            Download
                                                        </button>
                                                        <button
                                                            type="button"
                                                            onClick={handleDeleteAttachment}
                                                            className="flex items-center px-3 py-1.5 bg-red-50 text-red-600 text-xs font-bold rounded-lg hover:bg-red-100 transition-colors"
                                                        >
                                                            <Trash2 className="w-3.5 h-3.5 mr-1.5" />
                                                            Delete
                                                        </button>
                                                    </div>
                                                </div>
                                            ) : (
                                                <>
                                                    <span className="text-sm font-medium text-gray-700">
                                                        Click to upload or drag and drop
                                                    </span>
                                                    <span className="text-xs text-gray-500 mt-1">
                                                        PDF, JPG, PNG or WEBP (max. 25MB)
                                                    </span>
                                                </>
                                            )}
                                        </>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* ── Right: Sidebar (emergency contact + declarations + submit) ──── */}
            <div className="space-y-6">
                <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 space-y-6">
                    <SectionTitle icon={Heart} title="Emergency & Confirmation" />

                    {/* Emergency contact fields */}
                    <div className="space-y-4">
                        {[
                            {
                                name: 'emergencyContactName',
                                label: 'Contact Name',
                                type: 'text',
                                required: true,
                            },
                            {
                                name: 'emergencyContactPhone',
                                label: 'Contact Phone',
                                type: 'text',
                                required: true,
                            },
                            {
                                name: 'emergencyContactRelationship',
                                label: 'Relationship',
                                type: 'text',
                                required: true,
                            },
                            {
                                name: 'emergencyContactEmail',
                                label: 'Contact Email',
                                type: 'email',
                                required: false,
                            },
                        ].map(field => (
                            <div key={field.name}>
                                <Label
                                    required={field.required}
                                    error={errors[field.name] && touched[field.name]}
                                >
                                    {field.label}
                                </Label>
                                <input
                                    type={field.type}
                                    name={field.name}
                                    placeholder={
                                        field.name === 'emergencyContactName' ? 'Full Name of the contact' :
                                            field.name === 'emergencyContactPhone' ? 'e.g. +91 98765 43210' :
                                                field.name === 'emergencyContactRelationship' ? 'e.g. Father, Mother, Spouse' :
                                                    'e.g. contact@example.com'
                                    }
                                    className={`w-full px-3 py-2 border rounded-lg text-sm placeholder:text-gray-400 focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all ${errors[field.name] && touched[field.name]
                                        ? 'border-red-300 bg-red-50/10'
                                        : 'border-gray-200'
                                        }`}
                                    value={form[field.name]}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                />
                                <div className="flex justify-between items-start">
                                    <ErrorMessage
                                        message={errors[field.name]}
                                        touched={touched[field.name]}
                                    />
                                    <CharCounter
                                        current={form[field.name].length}
                                        limit={CHAR_LIMITS[field.name]}
                                    />
                                </div>
                            </div>
                        ))}
                    </div>

                    {/* Declaration checkboxes */}
                    <div className="space-y-3 pt-4 border-t">
                        {[
                            { name: 'confirmVoluntary', text: 'I am resigning voluntarily.', required: true },
                            { name: 'confirmPolicy', text: 'I understand the policy.', required: true },
                            { name: 'confirmAccurate', text: 'Information provided is accurate.', required: true },
                            {
                                name: 'exitInterview',
                                text: 'I am willing to participate in exit interview.',
                                optional: true,
                            },
                        ].map(field => (
                            <div key={field.name} data-field-error={errors[field.name] && touched[field.name] ? '' : undefined}>
                                <label className="flex items-start cursor-pointer group">
                                    <input
                                        type="checkbox"
                                        name={field.name}
                                        className={`h-4 w-4 rounded mt-0.5 ${errors[field.name] && touched[field.name]
                                            ? 'accent-red-500 ring-1 ring-red-400'
                                            : 'text-blue-600'
                                            }`}
                                        checked={form[field.name]}
                                        onChange={handleChange}
                                    />
                                    <span className={`ml-3 text-sm transition-colors ${errors[field.name] && touched[field.name]
                                        ? 'text-red-600'
                                        : 'text-gray-600 group-hover:text-gray-900'
                                        }`}>
                                        {field.text}
                                        {field.required && !field.optional && (
                                            <span className="text-red-500 ml-0.5">*</span>
                                        )}
                                        {field.optional && (
                                            <span className="ml-1.5 text-[10px] font-semibold text-gray-400 uppercase tracking-wide">
                                                (Optional)
                                            </span>
                                        )}
                                    </span>
                                </label>
                                <ErrorMessage message={errors[field.name]} touched={touched[field.name]} />
                            </div>
                        ))}
                    </div>

                    {/* Submit button */}
                    <div className="pt-2">
                        <button
                            onClick={handleSubmit}
                            disabled={isSubmitDisabled}
                            className="w-full py-3 bg-blue-600 text-white rounded-xl font-semibold shadow-lg shadow-blue-200 hover:bg-blue-700 transition-all disabled:opacity-50 disabled:grayscale disabled:cursor-not-allowed"
                        >
                            {submitMutation.isPending ? (
                                <div className="flex items-center justify-center">
                                    <Loader2 className="w-5 h-5 animate-spin mr-2" />
                                    Submitting...
                                </div>
                            ) : (
                                'Submit Resignation'
                            )}
                        </button>
                        {hasActiveRequest && (
                            <p className="mt-2 text-center text-xs text-amber-600 font-medium">
                                You have an active request in history.
                            </p>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ResignationForm;
